/**
 * Single source of truth for all data-region definitions.
 *
 * Naming rule: region key = short geographic code (eu, us, asia, au, br, …).
 * Everything else derives from the key:
 *   databaseId      = {key}-data          (eu uses "(default)")
 *   storageSuffix   = -{key}-storage      (eu uses null)
 *   backupBucket    = senf_prod_{key}_daily_backups
 *   restoreBucket   = senf_prod_{key}_backups
 *   functionSuffix  = key.toUpperCase()   (eu gets no suffix)
 *
 * To add a new region:
 *   1. Add an entry to REGION_CONFIG below.
 *   2. Add a matching entry to REGION_UI_CONFIG below.
 *   3. Everything else (firebase.ts, Cloud Functions, Netlify, monitoring UI)
 *      picks it up automatically — no other file changes needed.
 *
 * NO Firebase SDK dependencies — importable by any module/runtime.
 */

import type { IconName } from "senf-atomic-design-system/src/components/core/icon/names";

export const REGION_CONFIG = {
  eu: {
    databaseId: "(default)",
    storageBucketSuffix: null, // null means use the default storage bucket
    computeRegion: "europe-west1",
  },
  us: {
    databaseId: "us-data",
    storageBucketSuffix: "-us-storage",
    computeRegion: "us-central1",
  },
} as const;

/** Display metadata for each region — used in the organization builder UI. */
export const REGION_UI_CONFIG = {
  eu: {
    flag: "🇪🇺",
    icon: "locales/en-EU",
    label: "Europe",
    sublabel: "EU",
    location: "Europe (multi-region)",
    compliance: "GDPR · EU data residency",
  },
  us: {
    flag: "🇺🇸",
    icon: "locales/en-US",
    label: "United States",
    sublabel: "US",
    location: "United States (multi-region)",
    compliance: "CCPA · US data residency",
  },
} as const satisfies Record<
  keyof typeof REGION_CONFIG,
  {
    flag: string;
    icon: IconName;
    label: string;
    sublabel: string;
    location: string;
    compliance: string;
  }
>;

export type DataRegion = keyof typeof REGION_CONFIG;

export const ALL_REGIONS = Object.keys(REGION_CONFIG) as DataRegion[];

export const ALL_DATABASE_IDS = ALL_REGIONS.map(
  (region) => REGION_CONFIG[region].databaseId,
);

export function getRegionSuffix(region: DataRegion): string {
  return region === "eu" ? "" : region.toUpperCase();
}

export function buildRegionalFunctionName(
  baseName: string,
  region: DataRegion,
): string {
  return `${baseName}${getRegionSuffix(region)}`;
}

export function regionToDbId(region?: string): string {
  if (region && region in REGION_CONFIG) {
    return REGION_CONFIG[region as DataRegion].databaseId;
  }
  return REGION_CONFIG.eu.databaseId;
}

export function dbIdToRegion(databaseId?: string): DataRegion {
  for (const region of ALL_REGIONS) {
    if (REGION_CONFIG[region].databaseId === databaseId) return region;
  }
  return "eu";
}
